import apiClient from './apiClient';
import { Achievement, StudentAchievement, AchievementStats, AchievementType } from '../types/achievement';

// Re-export the types so they can be imported from this service
export type { Achievement, StudentAchievement, AchievementStats, AchievementType };

// Get all achievements
export const getAllAchievements = async (): Promise<Achievement[]> => {
  try {
    const response = await apiClient.get('/api/achievements');
    return response.data;
  } catch (error) {
    console.error('Error fetching achievements:', error);
    return [];
  }
};

// Get all achievement types
export const getAllAchievementTypes = async (): Promise<AchievementType[]> => {
  try {
    const response = await apiClient.get('/api/achievements/types');
    return response.data;
  } catch (error) {
    console.error('Error fetching achievement types:', error);
    return [];
  }
};

// Get user's achievements
export const getUserAchievements = async (userId?: number): Promise<StudentAchievement[]> => {
  try {
    // Determine endpoint based on if a userId is provided
    const endpoint = userId
      ? `/api/achievements/user/${userId}`
      : '/api/achievements/my-achievements';

    const response = await apiClient.get(endpoint);

    return response.data;
  } catch (error: unknown) {
    console.error('Error fetching user achievements:', error);
    const err = error as { response?: { data?: unknown }; message?: string };
    console.error('Error details:', err.response?.data || err.message);
    return [];
  }
};

// Test direct API endpoint for debugging
export const testApiEndpoint = async (endpoint: string) => {
  try {
    const response = await apiClient.get(endpoint);
    return {
      success: true,
      status: response.status,
      data: response.data
    };
  } catch (error: unknown) {
    const err = error as { response?: { status?: number; data?: unknown }; message?: string };
    return {
      success: false,
      status: err.response?.status,
      error: err.message,
      data: err.response?.data
    };
  }
};

// Check and potentially award new achievements
export const checkForNewAchievements = async (): Promise<StudentAchievement[]> => {
  try {
    const response = await apiClient.post('/api/achievements/check');
    return response.data;
  } catch (error) {
    console.error('Error checking for new achievements:', error);
    return [];
  }
};

// Get achievement statistics for the current user
export const getAchievementStats = async (): Promise<AchievementStats> => {
  try {
    const [allAchievements, userAchievements] = await Promise.all([
      getAllAchievements(),
      getUserAchievements()
    ]);
    
    // Calculate statistics
    const totalEarned = userAchievements.length;
    const percentageEarned = allAchievements.length > 0
      ? Math.round((totalEarned / allAchievements.length) * 100)
      : 0;
    
    // Sort by awarded date to get most recent
    const recentlyEarned = [...userAchievements]
      .sort((a, b) => new Date(b.awardedAt).getTime() - new Date(a.awardedAt).getTime())
      .slice(0, 3);
    
    // For highest rarity, we could calculate which achievement is least earned
    // But since we don't have that data here, we'll just return the most recent
    const highestRarity = recentlyEarned.length > 0 ? recentlyEarned[0] : null;
    
    return {
      totalEarned,
      percentageEarned,
      recentlyEarned,
      highestRarity
    };
  } catch (error) {
    console.error('Error getting achievement stats:', error);
    return {
      totalEarned: 0,
      percentageEarned: 0,
      recentlyEarned: [],
      highestRarity: null
    };
  }
};